import { ArticleTone } from '@/common/model/media/article'

export const ArticleDiagnosisApiPath = {
  DIAGNOSE_ARTICLE: '/api/sites/{site_id}/articles/{article_proposal_id}/diagnose', // POST Diagnose Article
  GENERATE_ARTICLE_REWRITE_STREAM:
    '/api/sites/{site_id}/articles/{article_proposal_id}/rewrite/stream' // POST Generate Article Rewrite Stream
}

export type ArticleDiagnosisApiPath =
  (typeof ArticleDiagnosisApiPath)[keyof typeof ArticleDiagnosisApiPath]

// -----------------------------------------------
// 診断（/articles/{article_proposal_id}/diagnose）
// -----------------------------------------------

export interface DiagnoseArticleVars {
  site_id: number
  article_proposal_id: number
}

export interface ArticleDiagnosisSeoScore {
  total: number
  summary?: string
  breakdown?: Record<string, number>
}

export interface ArticleDiagnosisImprovement {
  priority: 'high' | 'medium' | 'low'
  category?: string
  issue: string
  suggestion?: string
  expected_impact?: string
}

export interface ArticleDiagnosisActionStep {
  action: string
  effort: 'small' | 'medium' | 'large' | string
  impact: 'high' | 'medium' | 'low' | string
}

export interface ArticleDiagnosisResult {
  seo_score?: ArticleDiagnosisSeoScore
  improvements?: ArticleDiagnosisImprovement[]
  action_plan?: ArticleDiagnosisActionStep[]
  error?: string
}

export interface DiagnoseArticleResponse {
  article_proposal_id: number
  article_title: string
  metrics: Record<string, unknown>
  diagnosis: ArticleDiagnosisResult
}

// -----------------------------------------------
// リライト案（/articles/{article_proposal_id}/rewrite/stream）
// -----------------------------------------------

export interface GenerateArticleRewriteStreamVars {
  site_id: number
  article_proposal_id: number
  instruction?: string | null
  // 記事編集フォームの現在のトーン・ペルソナ。未指定なら保存済みの値が使われる
  tone?: ArticleTone
  persona?: string
}

export interface ArticleTitleRewrite {
  original?: string
  rewritten: string
  reason?: string
  seo_keywords?: string[]
}

export interface ArticleMetaDescriptionRewrite {
  original?: string
  rewritten: string
  reason?: string
}

// 本文を書き換える提案の反映先（コンテンツエリア上部／下部）。
// 欠落・不正な場合は上部として扱う（articleRewriteApply.ts）。
export type ArticleRewriteTargetArea = 'content_area_top' | 'content_area_bottom'

export interface ArticleHeadingRewrite {
  target_area?: ArticleRewriteTargetArea
  original?: string
  rewritten: string
  level?: string
  reason?: string
}

export interface ArticleNewSectionSuggestion {
  target_area?: ArticleRewriteTargetArea
  heading: string
  level?: string
  // 本文へそのまま追記されるセクションHTML（記事HTMLの型が登録されていれば型に沿った構造になる）
  section_html?: string
  placement?: string
  reason?: string
}

export interface ArticleCopyImprovement {
  target_area?: ArticleRewriteTargetArea
  area?: string
  before_hint?: string
  after_example?: string
  reason?: string
}

export interface ArticleRewriteResult {
  title_rewrites?: ArticleTitleRewrite[]
  meta_description_rewrites?: ArticleMetaDescriptionRewrite[]
  heading_rewrites?: ArticleHeadingRewrite[]
  new_sections?: ArticleNewSectionSuggestion[]
  copy_improvements?: ArticleCopyImprovement[]
  error?: string
}

export interface GenerateArticleRewriteStreamResult {
  article_proposal_id: number
  article_title: string
  rewrite: ArticleRewriteResult
  error?: string
}

// SSE progress イベント
export interface ArticleRewriteProgressEvent {
  step: number
  total_steps: number
  percent: number
  message: string
  detail?: string
}
